import { useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { useFormikContext } from 'formik';
import Button from 'components/new-ui/base/button/Button';
import CardWithIcon from 'components/new-ui/base/card/CardWithIcon';
import { AppliedAdditionalCharge } from 'store/invoices/types';
import { useRequestPromise } from 'hooks/useRequest';
import { listAdditionalCharges } from 'store/invoice-config/client';
import useDebouncedValue from 'hooks/useDebouncedValue';
import { InvoiceFieldValues } from 'views/invoices/types';
import AppliedAdditionalChargesTable from './AppliedAdditionalChargesTable';
import RemoveAdditionalChargeModal from './RemoveAdditionalChargeModal';
import SelectChargeModal, { ChargeSelectOption } from './SelectChargeModal';
import AdditionalChargeOptionLabel from './AdditionalChargeOptionLabel';

type Props = {
    isReadOnly: boolean;
    liabilityGross: number;
};

const AdditionalChargesSection = ({ isReadOnly, liabilityGross }: Props) => {
    const formik = useFormikContext<InvoiceFieldValues>();
    const [showAdditionalChargesModal, setShowAdditionalChargesModal] = useState(false);
    const [showConfirmRemoveChargeModal, setShowConfirmRemoveChargeModal] = useState(false);
    const [charge, setCharge] = useState<AppliedAdditionalCharge | undefined>(undefined);
    const [query, setQuery] = useState<string>();
    const debouncedQuery = useDebouncedValue(query);

    const { data, isLoading } = useRequestPromise(
        () => listAdditionalCharges({ query: debouncedQuery, archived: false }),
        undefined,
        [debouncedQuery]
    );

    const chargesOptions: ChargeSelectOption[] =
        data?.list?.map((el) => {
            return {
                id: el.id,
                value: el.id,
                searchText: `${el.productCode} ${el.description} ${el.taxCode} ${el.nominalCode}`.toLowerCase(),
                rawData: el,
                label: <AdditionalChargeOptionLabel charge={el} />,
            };
        }) ?? [];

    const handleAddCharge = (selected: ChargeSelectOption) => {
        if (!selected.rawData) return;

        const selectedChargeData: AppliedAdditionalCharge = selected.rawData;
        const current = formik.values.additionalCharges;
        const existingIndex = current.findIndex(
            (c) => c.productCode === selectedChargeData.productCode && c.customerId === selectedChargeData.customerId
        );

        if (existingIndex !== -1) {
            const updated = current.map((c, i) =>
                i === existingIndex ? { ...c, quantity: c.quantity + 1, active: true } : c
            );
            formik.setFieldValue('additionalCharges', updated, false);
        } else {
            const newCharge: AppliedAdditionalCharge = {
                id: selectedChargeData.id,
                quantity: 1,
                description: selectedChargeData.description,
                productCode: selectedChargeData.productCode,
                nominalCode: selectedChargeData.nominalCode,
                taxCode: selectedChargeData.taxCode,
                amount: selectedChargeData.amount,
                percentage: selectedChargeData.percentage,
                active: true,
                customerId: selectedChargeData.customerId,
            };
            formik.setFieldValue('additionalCharges', [...current, newCharge], false);
        }
    };

    const handleToggleCharge = (index: number) => {
        const current = formik.values.additionalCharges;
        const updated = current.map((c, i) => (i === index ? { ...c, active: !c.active } : c));
        formik.setFieldValue('additionalCharges', updated, false);
    };

    const handleRemoveCharge = () => {
        if (!charge) return;
        const updated = formik.values.additionalCharges.filter(
            (c) => !(c.productCode === charge.productCode && c.customerId === charge.customerId)
        );
        formik.setFieldValue('additionalCharges', updated, false);
        setShowConfirmRemoveChargeModal(false);
        setCharge(undefined);
    };

    return (
        <>
            <CardWithIcon
                gap="medium"
                layout="vertical"
                title="Additional Charges"
                iconProps={{
                    size: 32,
                    borderRadius: 8,
                    status: 'info',
                    icon: <PlusOutlined />,
                }}
                headerActions={
                    <>
                        {!isReadOnly && (
                            <Button
                                onClick={() => setShowAdditionalChargesModal(true)}
                                icon={<PlusOutlined />}
                                style={{ color: 'var(--brand-6)' }}
                                type="link"
                            >
                                Additional Charge
                            </Button>
                        )}
                    </>
                }
                children={
                    <>
                        {formik.values.additionalCharges.length > 0 && (
                            <AppliedAdditionalChargesTable
                                additionalCharges={formik.values.additionalCharges}
                                isReadOnly={isReadOnly}
                                liabilityGross={liabilityGross}
                                onToggle={handleToggleCharge}
                                onDelete={(record) => {
                                    setCharge(record);
                                    setShowConfirmRemoveChargeModal(true);
                                }}
                            />
                        )}
                    </>
                }
            />

            <SelectChargeModal
                open={showAdditionalChargesModal}
                charges={chargesOptions}
                isLoading={isLoading}
                onSearch={setQuery}
                onCancel={() => setShowAdditionalChargesModal(false)}
                onConfirm={handleAddCharge}
                onClear={() => setQuery(undefined)}
            />

            <RemoveAdditionalChargeModal
                open={showConfirmRemoveChargeModal}
                onConfirm={handleRemoveCharge}
                onClose={() => {
                    setShowConfirmRemoveChargeModal(false);
                    setCharge(undefined);
                }}
            />
        </>
    );
};

export default AdditionalChargesSection;
